/**
 * AccountMappingTab — Sprint 2, Feb 2026.
 *
 * SB pflegt das Konten-Mapping pro Mandant:
 *   - Template-Auswahl (SKR03/SKR04 für DE, EKR für AT, KMU für CH)
 *   - DATEV-Berater-Nr / Mandant-Nr
 *   - FL-Kategorie → Konto-Mapping-Tabelle
 *   - Auto-Vorschlag-Button für leere Kategorien
 *
 * Backend:
 *   GET  /api/advisor/clients/{cid}/account-mapping
 *   PUT  /api/advisor/clients/{cid}/account-mapping
 *   POST /api/advisor/clients/{cid}/account-mapping/auto-fill
 *   GET  /api/account-charts/{template_id}
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sparkles, Save, Loader2, Wand2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRY_DEFAULT_TAX_KEY = { DE: 'VSt19', AT: 'UST20', CH: 'VST_8.1' };

const H = () => {
  const t = localStorage.getItem('advisor_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export function AccountMappingTab({ clientId }) {
  const [mapping, setMapping] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [taxKeys, setTaxKeys] = useState([]);
  const [draft, setDraft] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoFilling, setAutoFilling] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, t] = await Promise.all([
        axios.get(`${API}/advisor/clients/${clientId}/account-mapping`, { headers: H() }),
        axios.get(`${API}/account-charts`),
      ]);
      setMapping(m.data);
      setTemplates(t.data.templates || []);
      setTaxKeys(m.data.tax_keys || []);
      setDraft({
        chart_template: m.data.chart_template,
        consultant_number: m.data.consultant_number || '',
        client_number: m.data.client_number || '',
        category_to_account: { ...(m.data.category_to_account || {}) },
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mapping konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  // Lade Konten für das gewählte Template
  useEffect(() => {
    if (!draft.chart_template) return;
    axios.get(`${API}/account-charts/${draft.chart_template}`)
      .then((r) => setAccounts(r.data.accounts || []))
      .catch(() => setAccounts([]));
  }, [draft.chart_template]);

  useEffect(() => { load(); }, [load]);

  const clientCountry = mapping?.country || 'DE';
  const countryDefaultKey = COUNTRY_DEFAULT_TAX_KEY[clientCountry] || 'VSt19';

  const setVal = (key) => (v) => {
    setDraft((d) => ({ ...d, [key]: v }));
    setDirty(true);
  };

  const setMappingForCategory = (cat, accountNumber) => {
    const acc = accounts.find((a) => a.number === accountNumber);
    setDraft((d) => ({
      ...d,
      category_to_account: {
        ...d.category_to_account,
        [cat]: accountNumber
          ? { account: accountNumber, tax_key: acc?.default_tax_key || countryDefaultKey, auto: false }
          : undefined,
      },
    }));
    setDirty(true);
  };

  // Steuerschlüssel pro Kategorie editierbar (Juni 2026)
  const setTaxKeyForCategory = (cat, taxKey) => {
    setDraft((d) => {
      const cur = d.category_to_account?.[cat];
      if (!cur?.account) return d;
      return {
        ...d,
        category_to_account: {
          ...d.category_to_account,
          [cat]: { ...cur, tax_key: taxKey, auto: false },
        },
      };
    });
    setDirty(true);
  };

  const removeMappingForCategory = (cat) => {
    const next = { ...draft.category_to_account };
    delete next[cat];
    setDraft((d) => ({ ...d, category_to_account: next }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Strip undefined values
      const cm = Object.fromEntries(Object.entries(draft.category_to_account || {}).filter(([, v]) => v && v.account));
      await axios.put(`${API}/advisor/clients/${clientId}/account-mapping`, {
        chart_template: draft.chart_template,
        consultant_number: draft.consultant_number,
        client_number: draft.client_number,
        category_to_account: cm,
      }, { headers: H() });
      toast.success('Mapping gespeichert');
      setDirty(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const autoFill = async () => {
    setAutoFilling(true);
    try {
      // Bug-Fix Juni 2026: ungespeicherte Änderungen (insb. Template-Wechsel)
      // ZUERST persistieren, damit der Auto-Vorschlag den GEWÄHLTEN Kontenrahmen
      // nutzt und das anschließende Neuladen die Auswahl nicht zurücksetzt.
      if (dirty) {
        const cm = Object.fromEntries(Object.entries(draft.category_to_account || {}).filter(([, v]) => v && v.account));
        await axios.put(`${API}/advisor/clients/${clientId}/account-mapping`, {
          chart_template: draft.chart_template,
          consultant_number: draft.consultant_number,
          client_number: draft.client_number,
          category_to_account: cm,
        }, { headers: H() });
        setDirty(false);
      }
      const r = await axios.post(
        `${API}/advisor/clients/${clientId}/account-mapping/auto-fill`,
        { chart_template: draft.chart_template },
        { headers: H() },
      );
      toast.success(`${r.data._auto_added || 0} Kategorien automatisch zugeordnet`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Auto-Fill');
    } finally {
      setAutoFilling(false);
    }
  };

  const standardCategories = useMemo(
    () => mapping?.standard_categories || [],
    [mapping]
  );

  const accountOptions = useMemo(
    () => accounts.map((a) => ({ value: a.number, label: `${a.number} — ${a.name}`, taxKey: a.default_tax_key })),
    [accounts]
  );

  // Steuerschlüssel-Label/Satz-Lookup (für Anzeige)
  const taxKeyLabel = useCallback((key) => {
    if (!key) return '—';
    const tk = taxKeys.find((t) => t.key === key);
    if (!tk) return key;
    return tk.rate != null ? `${key} · ${tk.rate} %` : key;
  }, [taxKeys]);

  // Cross-Country-Erkennung: gewähltes Template ≠ Mandanten-Land (erlaubt + Warnung)
  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === draft.chart_template),
    [templates, draft.chart_template]
  );
  const crossCountry = selectedTemplate && mapping?.country && selectedTemplate.country !== mapping.country;

  // Auto-Korrektur-Vorschlag: erstes Template, das zum Mandanten-Land passt (Juni 2026).
  const suggestedTemplate = useMemo(
    () => templates.find((t) => t.country === (mapping?.country || 'DE')),
    [templates, mapping]
  );
  const applyCountryTemplate = () => {
    if (suggestedTemplate) setVal('chart_template')(suggestedTemplate.id);
  };

  if (loading) {
    return <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-sm text-slate-500">Mapping laden…</div>;
  }

  // Quote = Anteil der Standardkategorien, die ein Konto haben (max. 100 %).
  // Verwaiste Mapping-Einträge (z.B. gelöschte Custom-Kategorien) werden NICHT
  // mitgezählt – sonst entstünden unintuitive Werte wie 115 %.
  const totalCats = standardCategories.length;
  const cm = draft.category_to_account || {};
  const mappedCount = standardCategories.filter((c) => cm[c.key]?.account).length;
  const ratio = totalCats > 0 ? Math.round(mappedCount / totalCats * 100) : 0;

  return (
    <div className="space-y-4" data-testid="account-mapping-tab">
      {/* Header / Template Selection */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-slate-900 mb-1">Kontenrahmen-Mapping</h3>
            <p className="text-xs text-slate-500">
              Übersetzt FL-Kategorien in echte Buchhaltungs-Konten für den DATEV/BMD-Export.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Mapping-Quote</div>
            <div className="text-2xl font-mono font-bold text-slate-900" data-testid="mapping-ratio">{ratio}%</div>
            <div className="text-[11px] text-slate-400">{mappedCount} / {totalCats}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs font-medium text-slate-700 mb-1 block">
              Kontenrahmen-Template
              <span className="ml-1 text-slate-400 font-normal">· Mandant: {clientCountry}</span>
            </Label>
            <select
              value={draft.chart_template || ''}
              onChange={(e) => setVal('chart_template')(e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md ${crossCountry ? 'border-amber-400 bg-amber-50' : 'border-zinc-200'}`}
              data-testid="account-template-select"
            >
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.country} · {t.code} ({t.account_count} Konten)
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 mb-1 block">DATEV-Berater-Nr.</Label>
            <Input
              value={draft.consultant_number || ''}
              onChange={(e) => setVal('consultant_number')(e.target.value)}
              placeholder="z.B. 12345"
              data-testid="consultant-number"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 mb-1 block">DATEV-Mandant-Nr.</Label>
            <Input
              value={draft.client_number || ''}
              onChange={(e) => setVal('client_number')(e.target.value)}
              placeholder="z.B. 67890"
              data-testid="client-number"
            />
          </div>
        </div>

        {crossCountry && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5" data-testid="cross-country-warning">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-amber-800 leading-snug">
                <strong>Achtung:</strong> Das gewählte Template ist für <strong>{selectedTemplate.country}</strong>,
                der Mandant sitzt aber in <strong>{clientCountry}</strong>. Die vorgeschlagenen Steuerschlüssel
                (z.&nbsp;B. {clientCountry === 'AT' ? '20 % statt 19 %' : 'Sätze'}) passen dann ggf. nicht.
                Bitte ein {clientCountry}-Template wählen oder die Steuerschlüssel manuell korrigieren.
              </p>
              {suggestedTemplate && (
                <Button
                  onClick={applyCountryTemplate}
                  variant="outline"
                  className="mt-2 h-7 px-2.5 text-xs text-amber-800 border-amber-400 bg-white hover:bg-amber-100"
                  data-testid="fix-country-template-btn"
                >
                  <Wand2 className="w-3 h-3 mr-1.5" />
                  Auf {suggestedTemplate.country}-Template umstellen ({suggestedTemplate.code})
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mapping Table */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Kategorien-Mapping</h3>
          <Button
            onClick={autoFill}
            disabled={autoFilling}
            variant="outline"
            className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
            data-testid="auto-fill-btn"
          >
            {autoFilling ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1.5" />}
            Auto-Vorschlag
          </Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Kategorie (FL)</th>
              <th className="px-4 py-2 text-left">Konto</th>
              <th className="px-4 py-2 text-left w-32">Steuerschlüssel</th>
              <th className="px-4 py-2 text-right w-12">—</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {standardCategories.map((cat) => {
              const catKey = cat.key;
              const catLabel = cat.label;
              const m = (draft.category_to_account || {})[catKey];
              const auto = m?.auto;
              return (
                <tr key={catKey} className="hover:bg-zinc-50/50" data-testid={`mapping-row-${catKey}`}>
                  <td className="px-4 py-2.5 text-slate-700 font-medium">
                    <div className="flex items-center gap-2">
                      {catLabel}
                      {auto && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded uppercase tracking-wider" title="Auto-vorgeschlagen">
                          <Sparkles className="w-2.5 h-2.5 inline mr-0.5" />Auto
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-400 font-mono">{catKey}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    <select
                      value={m?.account || ''}
                      onChange={(e) => setMappingForCategory(catKey, e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-zinc-200 rounded-md"
                      data-testid={`mapping-select-${catKey}`}
                    >
                      <option value="">— nicht zugeordnet —</option>
                      {accountOptions.map((a) => (
                        <option key={a.value} value={a.value}>{a.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-2.5">
                    {m?.account ? (
                      <select
                        value={m?.tax_key || ''}
                        onChange={(e) => setTaxKeyForCategory(catKey, e.target.value)}
                        className="w-full px-2 py-1.5 text-xs font-mono border border-zinc-200 rounded-md"
                        data-testid={`mapping-taxkey-${catKey}`}
                      >
                        {taxKeys.map((tk) => (
                          <option key={tk.key} value={tk.key}>
                            {tk.key}{tk.rate != null ? ` · ${tk.rate}%` : ''}
                          </option>
                        ))}
                        {/* Falls gespeicherter Schlüssel nicht im Länder-Set ist, trotzdem anzeigen */}
                        {m?.tax_key && !taxKeys.some((tk) => tk.key === m.tax_key) && (
                          <option value={m.tax_key}>{m.tax_key} (anderes Land)</option>
                        )}
                      </select>
                    ) : (
                      <span className="text-slate-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {m?.account && (
                      <button
                        onClick={() => removeMappingForCategory(catKey)}
                        className="text-slate-400 hover:text-rose-600"
                        title="Mapping entfernen"
                        data-testid={`mapping-remove-${catKey}`}
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Save */}
      <div className="flex items-center justify-end">
        <Button
          onClick={save}
          disabled={!dirty || saving}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="account-mapping-save"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Mapping speichern
        </Button>
      </div>
    </div>
  );
}

export default AccountMappingTab;
